import React from "react";
import { api, gcashQrUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { UploadCloud, BadgePercent, Trash2 } from "lucide-react";

const FIELDS = [
  { key: "hero_title", label: "Hero Title" },
  { key: "hero_subtitle", label: "Hero Subtitle", textarea: true },
  { key: "about_title", label: "About Title" },
  { key: "about_text", label: "About Text", textarea: true },
  { key: "owner_email", label: "Owner / Notification Email", help: "New bookings are emailed here" },
  { key: "gcash_account_name", label: "GCash Account Name" },
  { key: "gcash_number", label: "GCash Number" },
  { key: "downpayment_pct", label: "Downpayment %", number: true },
  { key: "max_guests", label: "Max Guests", number: true },
  { key: "contact_email", label: "Contact Email" },
  { key: "contact_phone", label: "Contact Phone" },
  { key: "location", label: "Location" },
  { key: "instagram", label: "Instagram" },
  { key: "facebook", label: "Facebook Page URL", help: "Shown to guests who book without an email (e.g., https://facebook.com/YourPage)" },
];

export default function AdminSettings() {
  const [s, setS] = React.useState(null);
  const [qrFile, setQrFile] = React.useState(null);
  const [qrVer, setQrVer] = React.useState(0);

  const load = () => api.get("/settings").then((r) => setS(r.data));
  React.useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...s };
      if (payload.downpayment_pct) payload.downpayment_pct = Number(payload.downpayment_pct);
      if (payload.max_guests) payload.max_guests = Number(payload.max_guests);
      await api.put("/settings", payload);
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const uploadQr = async () => {
    if (!qrFile) return toast.error("Choose a QR image");
    const fd = new FormData();
    fd.append("file", qrFile);
    try {
      await api.post("/settings/gcash-qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("QR uploaded");
      setQrFile(null);
      setQrVer((v) => v + 1);
      load();
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  if (!s) return <div className="text-[#A1A4B5]">Loading…</div>;

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-8">
        <p className="overline mb-2">Configuration</p>
        <h1 className="font-serif-display text-4xl">Settings</h1>
      </div>

      <div className="grid md:grid-cols-2 gap-5 border border-[var(--border)] rounded-2xl p-6 bg-[#13151D]">
        {FIELDS.map((f) => (
          <div key={f.key} className={f.textarea ? "md:col-span-2" : ""}>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">{f.label}</Label>
            {f.textarea ? (
              <Textarea
                value={s[f.key] || ""}
                onChange={(e) => setS({ ...s, [f.key]: e.target.value })}
                rows={3}
                className="mt-2 bg-[#0A0B10] border-[var(--border)]"
                data-testid={`setting-${f.key}`}
              />
            ) : (
              <Input
                type={f.number ? "number" : "text"}
                value={s[f.key] || ""}
                onChange={(e) => setS({ ...s, [f.key]: e.target.value })}
                className="mt-2 bg-[#0A0B10] border-[var(--border)]"
                data-testid={`setting-${f.key}`}
              />
            )}
            {f.help && (
              <p className="text-[10px] text-[#A1A4B5] mt-1.5">{f.help}</p>
            )}
          </div>
        ))}
      </div>

      <Button onClick={save} className="gold-btn rounded-full mt-6 px-8" data-testid="settings-save-button">
        Save Settings
      </Button>

      <div className="mt-12 border border-[var(--border)] rounded-2xl p-6 bg-[#13151D]">
        <h3 className="font-serif-display text-xl">GCash QR Code</h3>
        <p className="text-[#A1A4B5] text-sm mt-1">
          Upload your GCash QR image. It will be displayed in the customer's payment dialog.
        </p>
        <div className="grid md:grid-cols-[200px_1fr] gap-6 mt-5 items-start">
          <div className="bg-white p-3 rounded-lg">
            {s.gcash_qr_path ? (
              <img
                src={`${gcashQrUrl()}?v=${qrVer}`}
                alt="QR"
                className="w-full aspect-square object-contain"
                data-testid="settings-qr-preview"
              />
            ) : (
              <div className="aspect-square grid place-items-center text-black text-xs text-center">
                No QR uploaded
              </div>
            )}
          </div>
          <div>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setQrFile(e.target.files?.[0] || null)}
              className="bg-[#0A0B10] border-[var(--border)]"
              data-testid="qr-file-input"
            />
            {qrFile && <p className="text-xs text-[#C5A059] mt-2">Selected: {qrFile.name}</p>}
            <Button onClick={uploadQr} className="gold-btn rounded-full mt-4" data-testid="qr-upload-button">
              <UploadCloud size={14} className="mr-2" /> Upload QR
            </Button>
          </div>
        </div>
      </div>

      <PromosPanel />
      <ApiAccessPanel />
      <AdminUsersPanel />
      <ApiTesterPanel />
    </div>
  );
}

function PromosPanel() {
  const [promos, setPromos] = React.useState([]);
  const [form, setForm] = React.useState({
    name: "",
    discount_type: "percent",
    value: "",
    start_date: "",
    end_date: "",
  });
  const [saving, setSaving] = React.useState(false);

  const load = () =>
    api.get("/admin/promos").then((r) => setPromos(r.data || [])).catch(() => {});
  React.useEffect(() => {
    load();
  }, []);

  const setQuick = (mode) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    if (mode === "today") {
      setForm((f) => ({ ...f, start_date: iso(now), end_date: iso(now) }));
    } else {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setForm((f) => ({ ...f, start_date: iso(first), end_date: iso(last) }));
    }
  };

  const add = async () => {
    if (!form.name.trim()) return toast.error("Enter a promo name");
    if (!form.value || Number(form.value) <= 0) return toast.error("Enter a discount value");
    if (!form.start_date || !form.end_date) return toast.error("Pick start and end dates");
    setSaving(true);
    try {
      await api.post("/admin/promos", { ...form, value: Number(form.value), active: true });
      toast.success("Promo created — booking prices update automatically");
      setForm({ name: "", discount_type: "percent", value: "", start_date: "", end_date: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create promo");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/promos/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Promo deactivated" : "Promo activated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promo "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/promos/${p.id}`);
      toast.success("Promo deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const fmtDisc = (p) =>
    p.discount_type === "percent" ? `${p.value}% off` : `₱${Number(p.value).toLocaleString()} off`;

  return (
    <div className="mt-12 border border-[var(--border)] rounded-2xl p-6 bg-[#13151D]" data-testid="promos-panel">
      <div className="flex items-start gap-3 mb-1">
        <BadgePercent size={20} className="text-[#C5A059] mt-1 flex-shrink-0" />
        <div>
          <h3 className="font-serif-display text-xl">Promos &amp; Discounts</h3>
          <p className="text-[#A1A4B5] text-sm mt-1">
            Set a percentage or fixed-amount discount for a day or a date range. The booking page
            applies it automatically — guests see the original price crossed out.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-5 items-end">
        <div className="lg:col-span-1">
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Promo Name</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Summer Sale"
            className="mt-2 bg-[#0A0B10] border-[var(--border)]"
            data-testid="promo-name-input"
          />
        </div>
        <div>
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Type</Label>
          <div className="flex mt-2 rounded-lg border border-[var(--border)] overflow-hidden">
            {[
              { v: "percent", l: "% Percent" },
              { v: "fixed", l: "₱ Fixed" },
            ].map((t) => (
              <button
                key={t.v}
                type="button"
                onClick={() => setForm({ ...form, discount_type: t.v })}
                data-testid={`promo-type-${t.v}`}
                className={`flex-1 text-xs py-2.5 transition-colors ${
                  form.discount_type === t.v
                    ? "bg-[#C5A059] text-black font-semibold"
                    : "bg-[#0A0B10] text-[#A1A4B5] hover:text-white"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
            {form.discount_type === "percent" ? "Discount %" : "Amount ₱"}
          </Label>
          <Input
            type="number"
            min="1"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            placeholder={form.discount_type === "percent" ? "10" : "1000"}
            className="mt-2 bg-[#0A0B10] border-[var(--border)]"
            data-testid="promo-value-input"
          />
        </div>
        <div>
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Start Date</Label>
          <Input
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="mt-2 bg-[#0A0B10] border-[var(--border)]"
            data-testid="promo-start-input"
          />
        </div>
        <div>
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">End Date</Label>
          <Input
            type="date"
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="mt-2 bg-[#0A0B10] border-[var(--border)]"
            data-testid="promo-end-input"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 flex-wrap">
        <Button onClick={add} disabled={saving} className="gold-btn rounded-full px-6" data-testid="promo-add-button">
          {saving ? "Creating…" : "Create Promo"}
        </Button>
        <button
          type="button"
          onClick={() => setQuick("today")}
          className="text-xs text-[#A1A4B5] hover:text-[#C5A059] border border-[var(--border)] rounded-full px-3 py-1.5 transition-colors"
          data-testid="promo-quick-today"
        >
          Today only
        </button>
        <button
          type="button"
          onClick={() => setQuick("month")}
          className="text-xs text-[#A1A4B5] hover:text-[#C5A059] border border-[var(--border)] rounded-full px-3 py-1.5 transition-colors"
          data-testid="promo-quick-month"
        >
          Whole month
        </button>
      </div>

      <div className="mt-6 space-y-2" data-testid="promo-list">
        {promos.length === 0 && (
          <p className="text-[#A1A4B5] text-sm">No promos yet. Create one above to start discounting bookings.</p>
        )}
        {promos.map((p) => (
          <div
            key={p.id}
            className={`flex items-center justify-between gap-3 flex-wrap rounded-xl border p-3.5 ${
              p.active ? "border-[#C5A059]/40 bg-[#C5A059]/5" : "border-[var(--border)] bg-[#0A0B10] opacity-60"
            }`}
            data-testid={`promo-row-${p.id}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-white truncate">{p.name}</span>
                <span className="text-[10px] uppercase tracking-[0.14em] text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-1.5 py-0.5 rounded">
                  {fmtDisc(p)}
                </span>
                {!p.active && (
                  <span className="text-[10px] uppercase tracking-[0.14em] text-[#A1A4B5] border border-[var(--border)] px-1.5 py-0.5 rounded">
                    Inactive
                  </span>
                )}
              </div>
              <div className="text-xs text-[#A1A4B5] mt-1">
                {p.start_date === p.end_date ? p.start_date : `${p.start_date} → ${p.end_date}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => toggleActive(p)}
                className="bg-transparent border-[var(--border)] text-xs h-8"
                data-testid={`promo-toggle-${p.id}`}
              >
                {p.active ? "Deactivate" : "Activate"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => remove(p)}
                className="bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10 h-8 px-2.5"
                data-testid={`promo-delete-${p.id}`}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApiAccessPanel() {
  const [apiKey, setApiKey] = React.useState("");
  const [reveal, setReveal] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [regenerating, setRegenerating] = React.useState(false);

  React.useEffect(() => {
    api
      .get("/admin/api-key")
      .then((r) => setApiKey(r.data?.api_key || ""))
      .catch(() => toast.error("Failed to load API key"))
      .finally(() => setLoading(false));
  }, []);

  const masked = apiKey ? apiKey.slice(0, 14) + "•".repeat(20) : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toast.success("API key copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate API key? Any AI tool / Make.com scenario using the old key will stop working until you update it.")) return;
    setRegenerating(true);
    try {
      const r = await api.post("/admin/api-key/regenerate");
      setApiKey(r.data?.api_key || "");
      toast.success("New API key generated");
    } catch {
      toast.error("Failed to regenerate");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="mt-12 border border-[var(--border)] rounded-2xl p-6 bg-[#13151D]" data-testid="api-access-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-serif-display text-xl">Public API · Make.com / AI</h3>
          <p className="text-[#A1A4B5] text-sm mt-1">
            Single API key for read access, availability checks, and creating bookings on behalf of customers via AI.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-[#A1A4B5] text-sm">Loading key…</p>
      ) : (
        <>
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">API Key</Label>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Input
              readOnly
              value={reveal ? apiKey : masked}
              className="bg-[#0A0B10] border-[var(--border)] font-mono text-sm flex-1 min-w-[280px]"
              data-testid="api-key-display"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => setReveal((v) => !v)}
              className="bg-transparent border-[var(--border)]"
              data-testid="api-key-reveal"
            >
              {reveal ? "Hide" : "Reveal"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={copy}
              className="bg-transparent border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10"
              data-testid="api-key-copy"
            >
              Copy
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={regenerate}
              disabled={regenerating}
              className="bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10"
              data-testid="api-key-regenerate"
            >
              {regenerating ? "Regenerating…" : "Regenerate"}
            </Button>
          </div>

          <div className="mt-6">
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Endpoints for Make.com</Label>
            <div className="mt-3 space-y-3 text-[12px] font-mono text-[#A1A4B5]">
              <EndpointRow method="GET" path="/api/public/availability?date=YYYY-MM-DD" desc="Returns available_packages + is_weekend + booked_packages" />
              <EndpointRow method="GET" path="/api/public/rates" desc="Current prices and downpayment %" />
              <EndpointRow method="GET" path="/api/public/bookings?status=&start=&end=" desc="List bookings (filterable)" />
              <EndpointRow method="POST" path="/api/public/bookings" desc="Create booking (JSON body). Requires gcash_photo_url. Sends approval email." />
              <EndpointRow method="POST" path="/api/public/messenger-webhook" desc="Drop a Messenger / ManyChat lead into the admin Inbox for triage + 1-click convert." />
            </div>
            <p className="text-[10px] text-[#A1A4B5] mt-3">
              Send the key as header: <span className="font-mono text-[#C5A059]">X-API-Key: {apiKey ? apiKey.slice(0, 18) + "…" : "[your-key]"}</span>
            </p>
            <p className="text-[10px] text-[#A1A4B5] mt-1">
              Base URL: <span className="font-mono text-[#C5A059]">https://ibizapoolsidevilla.com</span>
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function EndpointRow({ method, path, desc }) {
  const color = method === "POST" ? "text-emerald-300 border-emerald-500/40" : "text-blue-300 border-blue-500/40";
  return (
    <div className="flex items-start gap-3">
      <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 rounded ${color}`}>{method}</span>
      <div className="flex-1 min-w-0">
        <div className="text-white truncate">{path}</div>
        <div className="text-[10px] text-[#A1A4B5] mt-0.5">{desc}</div>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Admin Users Panel — multi-admin account management                  */
/* ------------------------------------------------------------------ */

function AdminUsersPanel() {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState({ username: "", password: "" });
  const [creating, setCreating] = React.useState(false);
  const [pwTarget, setPwTarget] = React.useState(null); // username currently rotating pw
  const [newPw, setNewPw] = React.useState("");

  const load = React.useCallback(() => {
    setLoading(true);
    api
      .get("/admin/users")
      .then((r) => setUsers(r.data || []))
      .catch(() => toast.error("Failed to load admins"))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  async function create() {
    if (!form.username.trim() || !form.password.trim()) {
      return toast.error("Username and password are required");
    }
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setCreating(true);
    try {
      await api.post("/admin/users", { username: form.username.trim(), password: form.password });
      toast.success(`Admin "${form.username}" created`);
      setForm({ username: "", password: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add admin");
    } finally {
      setCreating(false);
    }
  }

  const remove = async (u) => {
    if (!window.confirm(`Remove admin "${u}"? They will lose access immediately.`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(u)}`);
      toast.success(`Admin "${u}" removed`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const setPassword = async (u) => {
    if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.patch(`/admin/users/${encodeURIComponent(u)}/password`, { password: newPw });
      toast.success(`Password updated for "${u}"`);
      setPwTarget(null);
      setNewPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password");
    }
  };

  return (
    <div
      className="mt-8 border border-[var(--border)] rounded-2xl p-6 bg-[#13151D]"
      data-testid="admin-users-panel"
    >
      <h3 className="font-serif-display text-xl">Admin Accounts</h3>
      <p className="text-[#A1A4B5] text-sm mt-1">
        Add additional admin users so co-owners or managers can access this dashboard.
        Each admin can manage bookings, finance, and settings independently.
      </p>

      {/* Existing admins */}
      <div className="mt-5 border border-[var(--border)] rounded-xl overflow-hidden">
        {loading ? (
          <p className="text-[#A1A4B5] text-sm p-4">Loading…</p>
        ) : users.length === 0 ? (
          <p className="text-[#A1A4B5] text-sm p-4">No admins found.</p>
        ) : (
          users.map((u) => (
            <div
              key={u.username}
              data-testid={`admin-user-row-${u.username}`}
              className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--border)] last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{u.username}</span>
                  {u.is_self && (
                    <span className="text-[9px] uppercase tracking-[0.16em] text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-1.5 py-0.5 rounded">
                      You
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#A1A4B5] mt-0.5">
                  added {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </div>
              </div>
              {pwTarget === u.username ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="password"
                    placeholder="New password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    className="h-9 bg-[#0A0B10] border-[var(--border)] text-sm"
                    data-testid={`new-password-${u.username}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => setPassword(u.username)}
                    className="gold-btn rounded-full"
                    data-testid={`save-password-${u.username}`}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setPwTarget(null); setNewPw(""); }}
                    className="bg-transparent border-[var(--border)]"
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setPwTarget(u.username); setNewPw(""); }}
                    className="bg-transparent border-[var(--border)] text-[#A1A4B5] hover:text-white"
                    data-testid={`reset-password-${u.username}`}
                  >
                    Reset Password
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remove(u.username)}
                    disabled={u.is_self}
                    className="bg-transparent border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-30"
                    data-testid={`remove-admin-${u.username}`}
                    title={u.is_self ? "You cannot remove your own account" : "Remove admin"}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add new admin */}
      <div className="mt-5 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div>
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Username</Label>
          <Input
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder="e.g. mariag"
            className="mt-2 bg-[#0A0B10] border-[var(--border)]"
            data-testid="new-admin-username"
          />
        </div>
        <div>
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Password</Label>
          <Input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="min. 6 characters"
            onKeyDown={(e) => { if (e.key === "Enter") create(); }}
            className="mt-2 bg-[#0A0B10] border-[var(--border)]"
            data-testid="new-admin-password"
          />
        </div>
        <Button
          type="button"
          onClick={create}
          disabled={creating}
          className="gold-btn rounded-full px-6"
          data-testid="add-admin-submit"
        >
          {creating ? "Adding…" : "Add Admin"}
        </Button>
      </div>
      <p className="text-[10px] text-[#A1A4B5] mt-3">
        Username: letters / numbers / dot / dash / underscore — at least 3 characters. Share the
        login URL with the new admin: <span className="font-mono text-[#C5A059]">/admin/login</span>.
      </p>
    </div>
  );
}



/* ------------------------------------------------------------------ */
/* API Tester — quickly fire requests against the public Make.com API   */
/* directly from the admin UI so the owner can verify behaviour before  */
/* wiring up Make.com or an AI agent.                                   */
/* ------------------------------------------------------------------ */

const ENDPOINT_PRESETS = [
  {
    id: "avail",
    method: "GET",
    path: "/api/public/availability",
    label: "Check availability",
    defaultQuery: () => `date=${new Date().toISOString().slice(0, 10)}`,
    defaultBody: "",
  },
  {
    id: "rates",
    method: "GET",
    path: "/api/public/rates",
    label: "Get rates",
    defaultQuery: () => "",
    defaultBody: "",
  },
  {
    id: "list",
    method: "GET",
    path: "/api/public/bookings",
    label: "List bookings",
    defaultQuery: () => "status=pending",
    defaultBody: "",
  },
  {
    id: "create",
    method: "POST",
    path: "/api/public/bookings",
    label: "Create booking",
    defaultQuery: () => "",
    defaultBody: () =>
      JSON.stringify(
        {
          guest_name: "Test Booking from Admin Tester",
          contact_number: "+639171234567",
          guest_email: "tester@example.com",
          package_type: "overnight",
          date: nextMonday(),
          num_days: 1,
          num_guests: 2,
          special_requests: "(API tester — feel free to reject)",
          gcash_photo_url: "https://example.com/dummy-receipt.jpg",
          gcash_reference: "TESTER-0001",
          messenger_subscriber_id: "",
        },
        null,
        2
      ),
  },
  {
    id: "messenger",
    method: "POST",
    path: "/api/public/messenger-webhook",
    label: "Messenger lead",
    defaultQuery: () => "",
    defaultBody: () =>
      JSON.stringify(
        {
          name: "Maria from Messenger",
          contact: "+639170000123",
          message: "Hello po! Available ba kayo this weekend? May 6 kami.",
          source: "messenger",
          subscriber_id: "fb_psid_demo_001",
          requested_date: nextMonday(),
          requested_guests: 6,
        },
        null,
        2
      ),
  },
];

function nextMonday() {
  const d = new Date();
  const day = d.getDay(); // 0 Sun … 6 Sat
  const add = ((1 + 7 - day) % 7) || 7; // always future
  d.setDate(d.getDate() + add);
  return d.toISOString().slice(0, 10);
}

function ApiTesterPanel() {
  const [presetId, setPresetId] = React.useState(ENDPOINT_PRESETS[0].id);
  const preset = ENDPOINT_PRESETS.find((p) => p.id === presetId);
  const [query, setQuery] = React.useState("");
  const [body, setBody] = React.useState("");
  const [response, setResponse] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [history, setHistory] = React.useState([]);

  // Seed query/body whenever the preset changes
  React.useEffect(() => {
    if (!preset) return;
    setQuery(typeof preset.defaultQuery === "function" ? preset.defaultQuery() : preset.defaultQuery || "");
    setBody(typeof preset.defaultBody === "function" ? preset.defaultBody() : preset.defaultBody || "");
    setResponse(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId]);

  const run = async () => {
    if (!preset) return;
    setRunning(true);
    const started = performance.now();
    let resp;
    try {
      // Get the current API key from settings collection (re-fetched each run so a
      // freshly-regenerated key works without page reload).
      const k = await api.get("/admin/api-key");
      const apiKey = k.data?.api_key || "";
      if (!apiKey) {
        throw new Error("API key not configured yet — generate one above first.");
      }
      const cleanPath = preset.path.replace(/^\/api/, "");
      const url = cleanPath + (query ? `?${query.replace(/^\?/, "")}` : "");
      const opts = {
        headers: { "X-API-Key": apiKey },
        // The Make.com-style endpoints don't need the admin Bearer token, but axios
        // adds it via interceptor — backend ignores it so this is harmless.
      };
      let r;
      if (preset.method === "GET") {
        r = await api.get(url, opts);
      } else {
        const parsed = body.trim() ? JSON.parse(body) : {};
        r = await api.post(url, parsed, opts);
      }
      resp = { status: r.status, ok: true, data: r.data };
    } catch (e) {
      if (e?.message?.startsWith?.("Unexpected token")) {
        resp = { status: 0, ok: false, data: { error: "Invalid JSON body — please fix and retry.", detail: e.message } };
      } else {
        resp = {
          status: e?.response?.status || 0,
          ok: false,
          data: e?.response?.data || { error: e?.message || "Request failed" },
        };
      }
    } finally {
      const ms = Math.round(performance.now() - started);
      setResponse({ ...resp, ms });
      setHistory((h) =>
        [
          {
            id: `${Date.now()}`,
            method: preset.method,
            path: preset.path,
            status: resp?.status,
            ok: resp?.ok,
            ms,
            at: new Date().toLocaleTimeString(),
          },
          ...h,
        ].slice(0, 8)
      );
      setRunning(false);
    }
  };

  return (
    <div
      className="mt-8 border border-[var(--border)] rounded-2xl p-6 bg-[#13151D]"
      data-testid="api-tester-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-serif-display text-xl">API Sandbox · Test Your Make.com / AI Calls</h3>
          <p className="text-[#A1A4B5] text-sm mt-1">
            Fire any public-API request right from this page using the live API key. Useful
            for double-checking payloads before wiring up Make.com or an AI agent.
          </p>
        </div>
      </div>

      {/* Endpoint preset selector */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        {ENDPOINT_PRESETS.map((p) => {
          const active = p.id === presetId;
          return (
            <button
              key={p.id}
              type="button"
              data-testid={`api-tester-preset-${p.id}`}
              onClick={() => setPresetId(p.id)}
              className={`text-left p-3 rounded-xl border text-xs transition-all ${
                active
                  ? "border-[#C5A059] bg-[#C5A059]/10"
                  : "border-[var(--border)] hover:border-[#C5A059]/50"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 rounded ${
                    p.method === "POST"
                      ? "text-emerald-300 border-emerald-500/40"
                      : "text-blue-300 border-blue-500/40"
                  }`}
                >
                  {p.method}
                </span>
                <span className="font-medium">{p.label}</span>
              </div>
              <div className="font-mono text-[10px] text-[#A1A4B5] truncate mt-1">{p.path}</div>
            </button>
          );
        })}
      </div>

      {/* Query / Body editors */}
      {preset?.method === "GET" ? (
        <div>
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
            Query string <span className="normal-case tracking-normal text-[#A1A4B5]/70">(optional)</span>
          </Label>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="key=value&other=…"
            className="mt-2 bg-[#0A0B10] border-[var(--border)] font-mono text-sm"
            data-testid="api-tester-query"
          />
        </div>
      ) : (
        <div>
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">JSON Body</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="mt-2 bg-[#0A0B10] border-[var(--border)] font-mono text-[12px] leading-relaxed"
            data-testid="api-tester-body"
          />
        </div>
      )}

      <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
        <Button
          type="button"
          onClick={run}
          disabled={running}
          className="gold-btn rounded-full px-6"
          data-testid="api-tester-send"
        >
          {running ? "Sending…" : `Send ${preset?.method}`}
        </Button>
        {response && (
          <div className="text-xs text-[#A1A4B5]">
            <span
              className={`font-mono px-2 py-0.5 rounded ${
                response.ok ? "text-emerald-300 bg-emerald-500/10" : "text-rose-300 bg-rose-500/10"
              }`}
            >
              {response.status} {response.ok ? "OK" : "ERROR"}
            </span>
            <span className="ml-2">{response.ms} ms</span>
          </div>
        )}
      </div>

      {/* Response */}
      {response && (
        <div className="mt-4">
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Response</Label>
          <pre
            data-testid="api-tester-response"
            className="mt-2 max-h-80 overflow-auto rounded-xl bg-[#0A0B10] border border-[var(--border)] p-4 text-[11px] font-mono text-emerald-200 whitespace-pre-wrap break-all"
          >
{JSON.stringify(response.data, null, 2)}
          </pre>
        </div>
      )}

      {/* Call history */}
      {history.length > 0 && (
        <div className="mt-6">
          <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Recent Calls</Label>
          <div className="mt-2 space-y-1.5">
            {history.map((h) => (
              <div
                key={h.id}
                data-testid="api-tester-history-row"
                className="flex items-center justify-between text-[11px] font-mono text-[#A1A4B5] bg-[#0A0B10] border border-[var(--border)] rounded px-3 py-1.5"
              >
                <div className="flex items-center gap-2 truncate">
                  <span
                    className={`text-[9px] uppercase tracking-widest border px-1 py-0.5 rounded ${
                      h.method === "POST"
                        ? "text-emerald-300 border-emerald-500/40"
                        : "text-blue-300 border-blue-500/40"
                    }`}
                  >
                    {h.method}
                  </span>
                  <span className="truncate text-white">{h.path}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={h.ok ? "text-emerald-300" : "text-rose-300"}>{h.status}</span>
                  <span>{h.ms}ms</span>
                  <span>{h.at}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
